import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Play } from "lucide-react";

function NewsGrid({ items }) {
  if (items.length === 0) return <div className="py-16 text-center text-jpa-muted">No news items in this category.</div>;
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((n) => (
        <Link key={n.id} to={`/news/${n.id}`} data-testid={`news-card-${n.id}`} className="group block border border-jpa-border bg-white overflow-hidden hover:-translate-y-1 transition-transform">
          <div className="relative aspect-[16/10] overflow-hidden bg-jpa-surface">
            {n.image_url && <img src={n.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}
            {n.media_type === "video" && (
              <div className="absolute inset-0 grid place-items-center">
                <div className="w-14 h-14 bg-jpa-accent rounded-full grid place-items-center"><Play className="w-6 h-6 text-jpa-ink ml-1" /></div>
              </div>
            )}
            <span className="absolute top-3 left-3 bg-jpa-primary text-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest skew-badge">{n.category}</span>
          </div>
          <div className="p-5">
            <h3 className="font-display text-2xl tracking-wider group-hover:text-jpa-primary transition-colors line-clamp-2">{n.title}</h3>
            <p className="mt-2 text-sm text-jpa-muted line-clamp-2">{n.excerpt}</p>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-jpa-muted">{new Date(n.published_at).toLocaleDateString()} • {n.author}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function NewsPage() {
  const [news, setNews] = useState([]);
  useEffect(() => { api.get("/news").then((r) => setNews(r.data)); }, []);
  const tournament = news.filter((n) => n.category === "tournament");
  const national = news.filter((n) => n.category === "national");
  const international = news.filter((n) => n.category === "international");
  return (
    <div data-testid="news-page">
      <PageHero eyebrow="Press centre" title="NEWS & MEDIA" subtitle="Tournament coverage, national highlights and global pickleball news."
        image="https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=1800" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14">
        <Tabs defaultValue="all">
          <TabsList className="bg-jpa-surface rounded-sm h-auto p-1 flex-wrap">
            <TabsTrigger value="all" data-testid="tab-news-all" className="uppercase tracking-widest text-sm font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-6 py-2">All</TabsTrigger>
            <TabsTrigger value="tournament" data-testid="tab-news-tournament" className="uppercase tracking-widest text-sm font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-6 py-2">Tournament</TabsTrigger>
            <TabsTrigger value="national" data-testid="tab-news-national" className="uppercase tracking-widest text-sm font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-6 py-2">National</TabsTrigger>
            <TabsTrigger value="international" data-testid="tab-news-international" className="uppercase tracking-widest text-sm font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-6 py-2">International</TabsTrigger>
          </TabsList>
          <TabsContent value="all" className="mt-8"><NewsGrid items={news} /></TabsContent>
          <TabsContent value="tournament" className="mt-8"><NewsGrid items={tournament} /></TabsContent>
          <TabsContent value="national" className="mt-8"><NewsGrid items={national} /></TabsContent>
          <TabsContent value="international" className="mt-8"><NewsGrid items={international} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
